// src/pages/Support/components/TicketDetailView.js

import React, { useMemo, useState, useEffect } from "react"; // ✅ useState & useEffect added
import UserProfileCard from "./UserProfileCard.js";
import ChatArea from "./ChatArea.js";
import { Card } from "antd";
import { useSelector } from "react-redux";
import { css } from "emotion";

const TicketDetailView = ({ ticket, initialMessages = [], onBack }) => {
  const tokenInfoList = useSelector((state) => state.tokenInfoList);

  const [localTicket, setLocalTicket] = useState(ticket);

  useEffect(() => {
    if (ticket) {
      setLocalTicket(ticket);
    }
  }, [ticket]);

  // Get current logged-in user info
  const currentUser = useMemo(
    () => ({
      name:
        `${tokenInfoList?.info?.name?.firstName || ""} ${tokenInfoList?.info?.name?.lastName || ""}`.trim() ||
        "Admin User",
      role: tokenInfoList?.info?.role || "Administrator",
      _id: tokenInfoList?.info?._id,
    }),
    [tokenInfoList],
  );

  const handleStatusUpdate = (newStatus) => {
    setLocalTicket((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        status: newStatus,
      },
    }));
  };

  const userData = useMemo(() => {
    if (!localTicket) return null;
    const createdByUser = localTicket.data?.userId;
    const firstName =
      createdByUser?.name?.firstName || createdByUser?.name || "Unknown";
    const lastName = createdByUser?.name?.lastName || "";
    return {
      ticketId: localTicket?.data?.ticketId || "-",
      name: `${firstName} ${lastName}`.trim(),
      employeeId: createdByUser?.empId || "-",
      company: localTicket.data?.companyId?.companyName || "-",
      createdAt: localTicket.data?.created_at
        ? new Date(localTicket.data?.created_at).toLocaleDateString("en-US", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
        : "N/A",
      status: localTicket.data?.status,
      avatar: createdByUser?.profileImage?.url || createdByUser?.profileImage,
      email: createdByUser?.email || "N/A",
      _id: localTicket.data?._id,
      subject: localTicket.data?.subject,
      description: localTicket.data?.description,
      priority: localTicket.data?.priority,
      category: localTicket.data?.category,
      assignedTo: localTicket.data?.assignedEmployee,
    };
  }, [localTicket]);

  const mappedMessages = useMemo(() => {
    if (!Array.isArray(initialMessages) || initialMessages.length === 0) {
      return [];
    }

    return initialMessages.map((msg) => {
      const userInfo = msg.userId;
      let senderName = "Unknown User";
      let senderId = null;

      if (typeof userInfo === "object" && userInfo !== null) {
        if (userInfo.name) {
          senderName =
            `${userInfo.name.firstName || ""} ${userInfo.name.lastName || ""}`.trim();
        } else if (userInfo.type === "super-admin") {
          senderName = "Super Admin";
        } else {
          senderName = "Admin";
        }

        senderId = userInfo._id;

        if (msg.userModel === "admin" && !senderName.includes("Super")) {
          senderName = "Support Team";
        }
      } else if (typeof userInfo === "string") {
        senderName = msg.userModel === "admin" ? "Support Admin" : "User";
        senderId = userInfo;
      }

      const isAIMessage = msg.maxAI === true;
      const text = msg.messageBody || "";
      const hasFiles = msg.files && msg.files.length > 0;
      const AI_AVATAR = "/support/MaxAI.png";

      return {
        id: msg._id || `msg-${Date.now()}`,
        senderName: isAIMessage ? "Max AI" : senderName,
        text: text,
        timestamp: msg.createdAt
          ? `${new Date(msg.createdAt).toLocaleDateString("en-US", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })} · ${new Date(msg.createdAt).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
          })}`
          : "",
        isCurrentUser: senderId === currentUser._id,
        isAI: isAIMessage,
        avatar: isAIMessage
          ? AI_AVATAR
          : userInfo?.profileImage?.url ||
          (typeof userInfo?.profileImage === "string"
            ? userInfo.profileImage
            : null),
        initials: senderName
          ?.split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .slice(0, 2),
        files: hasFiles ? msg.files : [],
        originalMessage: msg,
      };
    });
  }, [initialMessages, currentUser._id]);

  if (!localTicket) return null;

  return (
    <div
      className="ticket-detail-enterprise"
      style={{
        display: "flex",
        height: "100vh",
        overflow: "hidden",
        animation: "fadeIn 0.3s ease-out",
        gap: 10,
      }}>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* LEFT SIDEBAR: User Profile Card */}
      <div
        style={{
          width: 320,
          minWidth: 280,
          maxWidth: 350,
          padding: "0 10px",
          alignSelf: "flex-start",
        }}>
        <UserProfileCard userData={userData} onBack={onBack} />
      </div>

      {/* RIGHT SIDE: Chat/Messaging Area */}
      <div
        className={css`
          .ant-card {
        padding:0px !important;
        }
        `}
        style={{
          flex: 1,
          display: "flex",
          minHeight: 0,
        }}>
        <Card
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
          bodyStyle={{
            padding: 0,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minHeight: 0,
          }}>
          <ChatArea
            ticket={localTicket?.data}
            currentUser={currentUser}
            initialMessages={mappedMessages}
            ticketId={localTicket?.data?._id}
            onTicketClose={handleStatusUpdate}
          />
        </Card>
      </div>
    </div>
  );
};

export default TicketDetailView;
